import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, CheckboxGroup, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, useStore, type StoreDetails } from '../catalog/shared.js';

type Carrier = Schemas['Carrier'];

/** "7" or "7,5" (TND) → millimes; null when empty or invalid. */
export function dinarsToMinor(value: string): string | null {
  const clean = value.trim().replace(',', '.');
  if (!/^[0-9]{1,7}(\.[0-9]{1,3})?$/.test(clean)) return null;
  const [whole = '0', fraction = ''] = clean.split('.');
  return String(Number(whole) * 1000 + Number(fraction.padEnd(3, '0')));
}

/** Millimes → "7.5" (for editing). */
export function minorToDinars(minor: string): string {
  const value = Number(minor);
  return (value / 1000).toString();
}

function carrierError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError) {
    if (failure.code === 'conflict') return t('admin.carriers.error.conflict');
    if (failure.code === 'validation-failed') return t('admin.carriers.error.validation');
  }
  return errorMessage(t, failure);
}

/** Carriers the store works with: no carrier API, the merchant enters tracking numbers. */
export function CarriersPage() {
  const { storeId = '' } = useParams();
  const { t, locale } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [carriers, setCarriers] = useState<Carrier[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/carriers', { params: { path: { storeId } } }),
    );
    setCarriers(result.items);
  }, [storeId]);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [load, t]);

  const failed = storeError ?? error;
  if (failed !== null) return <Alert tone="error">{failed}</Alert>;
  if (store === null || carriers === null) return <Loading />;
  const canManage = store.permissions.includes('checkout.manage');
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.carriers.title')}</h1>
      <p>{t('admin.carriers.intro')}</p>
      {carriers.length === 0 ? <p className="mrigl-muted">{t('admin.carriers.none')}</p> : null}
      {carriers.map((carrier) => (
        <CarrierForm
          key={`${carrier.carrierId}-${String(carrier.version)}`}
          store={store}
          carrier={carrier}
          canManage={canManage}
          onSaved={load}
        />
      ))}
      {canManage ? <CarrierForm store={store} carrier={null} canManage onSaved={load} /> : null}
    </div>
  );
}

function CarrierForm({
  store,
  carrier,
  canManage,
  onSaved,
}: {
  readonly store: StoreDetails;
  readonly carrier: Carrier | null;
  readonly canManage: boolean;
  readonly onSaved: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(carrier?.name ?? '');
  const [phone, setPhone] = useState(carrier?.phone ?? '');
  const [url, setUrl] = useState(carrier?.trackingUrlTemplate ?? '');
  const [fee, setFee] = useState(
    carrier?.defaultFee === null || carrier === null ? '' : minorToDinars(carrier.defaultFee.minor),
  );
  const [active, setActive] = useState(carrier?.active ?? true);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const label =
    carrier === null ? t('admin.carriers.add') : t('admin.carriers.edit', { name: carrier.name });

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice(null);
    setError(null);
    const defaultFee = fee.trim() === '' ? null : dinarsToMinor(fee);
    if (fee.trim() !== '' && defaultFee === null) {
      setError(t('admin.carriers.error.validation'));
      return;
    }
    const body = {
      name: name.trim(),
      phone: phone.trim() === '' ? null : phone.trim(),
      trackingUrlTemplate: url.trim() === '' ? null : url.trim(),
      defaultFee: defaultFee === null ? null : { minor: defaultFee, currency: 'TND' as const },
      active,
    };
    setBusy(true);
    try {
      if (carrier === null) {
        await unwrap(
          api.POST('/admin-api/v1/stores/{storeId}/carriers', {
            params: { path: { storeId: store.storeId } },
            body,
          }),
        );
        setName('');
        setPhone('');
        setUrl('');
        setFee('');
        setNotice(t('admin.carriers.added'));
      } else {
        await unwrap(
          api.PUT('/admin-api/v1/stores/{storeId}/carriers/{carrierId}', {
            params: { path: { storeId: store.storeId, carrierId: carrier.carrierId } },
            body: { ...body, version: carrier.version },
          }),
        );
        setNotice(t('admin.carriers.saved'));
      }
      await onSaved();
    } catch (failure) {
      setError(carrierError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-card mrigl-stack"
      aria-label={label}
      onSubmit={(event) => void save(event)}
    >
      {carrier === null ? null : (
        <h2 className="mrigl-subheading" dir="auto">
          {carrier.name}
          {carrier.active ? null : ` (${t('admin.carriers.inactive')})`}
        </h2>
      )}
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <fieldset className="mrigl-stack" disabled={!canManage}>
        <TextField
          label={t('admin.carriers.name')}
          required
          maxLength={80}
          dir="auto"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <TextField
          label={t('admin.carriers.phone')}
          type="tel"
          dir="ltr"
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value);
          }}
        />
        <TextField
          label={t('admin.carriers.trackingUrl')}
          hint={t('admin.carriers.trackingUrlHint', { placeholder: '{tracking}' })}
          type="url"
          dir="ltr"
          maxLength={300}
          value={url}
          onChange={(event) => {
            setUrl(event.target.value);
          }}
        />
        <TextField
          label={t('admin.carriers.defaultFee')}
          inputMode="decimal"
          dir="ltr"
          value={fee}
          onChange={(event) => {
            setFee(event.target.value);
          }}
        />
        <CheckboxGroup
          legend={t('admin.carriers.active')}
          options={[{ value: 'active', label: t('admin.carriers.active') }]}
          values={active ? ['active'] : []}
          onChange={(values) => {
            setActive(values.includes('active'));
          }}
        />
      </fieldset>
      {canManage ? (
        <Button type="submit" busy={busy}>
          {carrier === null ? t('admin.carriers.add') : t('admin.save')}
        </Button>
      ) : null}
    </form>
  );
}
